import { type ZodType } from 'zod';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  schema?: ZodType<unknown>;
};

const BASE = import.meta.env.VITE_API_BASE_URL || '/api';

let sessionTokenProvider: () => string | null = () => null;
// Called whenever a response carries a `sessionToken` field (register/create/join),
// so AuthStore can persist it without every call site having to extract it by hand.
let sessionTokenListener: (token: string) => void = () => {};

export function setSessionTokenProvider(fn: () => string | null) {
  sessionTokenProvider = fn;
}

// Exposes the current per-tab token to other channels (SignalR's accessTokenFactory)
// that need the same identity as REST calls, rather than the shared cross-tab cookie.
export function getSessionToken(): string | null {
  return sessionTokenProvider();
}

export function setSessionTokenListener(fn: (token: string) => void) {
  sessionTokenListener = fn;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = sessionTokenProvider();
  if (token) headers['X-Session-Token'] = token;

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    let code = 'UNKNOWN_ERROR';
    let message = res.statusText;
    try {
      const j = await res.json();
      code = (j.code ?? j.error ?? code) as string;
      message = (j.message ?? message) as string;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, code, message);
  }

  if (res.status === 204) return undefined as T;
  const json = await res.json();
  const receivedToken = (json as { sessionToken?: unknown } | null)?.sessionToken;
  if (typeof receivedToken === 'string') sessionTokenListener(receivedToken);
  return (opts.schema ? opts.schema.parse(json) : json) as T;
}
